import { type Site, type SiteMenuOrder } from '../../../../entities';
import { type LocalDate } from '../../../../utils/local-date';

export interface CustomerOrderUpsertPayload {
  customerId: number;
  orderDate: string;
  /** The revision the edit started from, which the server checks is still the order's latest one */
  baseRevisionId?: number;
  sitesOrders: SiteOrdersPayload[];
}

interface SiteOrdersPayload {
  siteId: number;
  siteMenuOrders: SiteMenuOrderPayload[];
}

interface SiteMenuOrderPayload {
  scheduledMealId: number;
  routeId?: number;
  quantity: number;
  mainType: string;
  serviceType?: string;
  comment?: string;
  prepackBeverages?: boolean;
  diets: string[] | null;
  preferences: string[] | null;
  beverages: BeveragePayload[];
  supplies: SupplyPayload[];
}

interface BeveragePayload {
  id: number;
  quantity: number;
  isExtraBeverage?: boolean;
}

interface SupplyPayload {
  id: number;
  quantity: number;
}

/** Ids travel as strings on the client but the API takes numbers. */
// TODO: we should remove this once all the ids are numbers
const toId = (id: string | undefined): number | undefined =>
  id === undefined || id === '' ? undefined : Number(id);

const toSiteMenuOrderPayload = (siteMenuOrder: SiteMenuOrder): SiteMenuOrderPayload => ({
  scheduledMealId: toId(siteMenuOrder.scheduledMeal?.id)!,
  routeId: toId(siteMenuOrder.route?.id),
  quantity: siteMenuOrder.quantity,
  mainType: siteMenuOrder.mainType,
  serviceType: siteMenuOrder.serviceType,
  comment: siteMenuOrder.comment,
  prepackBeverages: siteMenuOrder.prepackBeverages,
  diets: siteMenuOrder.diets,
  preferences: siteMenuOrder.preferences,
  beverages: (siteMenuOrder.siteMenuBeverageOrders ?? []).map((beverageOrder) => ({
    id: toId(beverageOrder.beverage?.id)!,
    quantity: beverageOrder.quantity,
    isExtraBeverage: beverageOrder.isExtraBeverage,
  })),
  supplies: (siteMenuOrder.siteMenuSupplyOrders ?? []).map((supplyOrder) => ({
    id: toId(supplyOrder.supply?.id)!,
    quantity: supplyOrder.quantity,
  })),
});

/**
 * Builds the body for POST /v2/customer-order from the per-site orders the form holds. Sites with
 * nothing ordered are left out, since the API requires every site entry to carry at least one meal.
 *
 * "baseRevisionId" is the revision an edit started from. The server refuses the update when the
 * order has gained a newer revision since, so an edit can't silently discard someone else's.
 */
export const buildCustomerOrderUpsertPayload = (
  sites: Site[],
  customerId: string,
  orderDate: LocalDate,
  baseRevisionId?: string
): CustomerOrderUpsertPayload => ({
  customerId: toId(customerId)!,
  orderDate: orderDate.toString(),
  baseRevisionId: toId(baseRevisionId),
  sitesOrders: sites
    .filter((site) => (site.siteMenuOrders?.length ?? 0) > 0)
    .map((site) => ({
      siteId: toId(site.id)!,
      siteMenuOrders: (site.siteMenuOrders ?? []).map(toSiteMenuOrderPayload),
    })),
});
